import React, { useState } from "react";
import './TableRow.css';

const OwnershipTableRow = ({ label, values, onChange, tooltip }) => {
  // State to track the input mode (shares or percentage) for each row
  const [inputModes, setInputModes] = useState(values.map(() => null)); // Default to no selection
  // Store raw input strings locally to allow intermediate typing states like "0.0.."
  // Initialize from prop values if they exist
  const [rawInputs, setRawInputs] = useState(values.map((val) => {
    if (val.vc_shares != null && val.vc_shares !== undefined) {
      return String(val.vc_shares);
    } else if (val.percentage_ownership != null && val.percentage_ownership !== undefined) {
      return String(val.percentage_ownership);
    }
    return "";
  }));

  // Handles input changes based on the current input mode (shares or percentage)
  const handleInputChange = (index, inputValue) => {
    // Always update local raw input state so user can type freely
    const updatedRawInputs = [...rawInputs];
    updatedRawInputs[index] = inputValue;
    setRawInputs(updatedRawInputs);

    if (inputValue === "") {
      // Reset both fields if the input is empty
      onChange(index, "vc_shares", null);
      onChange(index, "percentage_ownership", null);
      return;
    }

    // Only allow numeric characters, dots, and optional sign
    if (!/^[-+]?[\d.]*$/.test(inputValue)) {
      return; // Invalid characters, don't update parent but keep the typed value
    }

    // Check if it's an intermediate typing state (ends with dot or has multiple dots)
    const isIntermediate = inputValue.endsWith('.') || /\.{2,}/.test(inputValue) || inputValue === "-" || inputValue === "+";
    
    // If it's intermediate, don't update parent state yet - let user finish typing
    if (isIntermediate) {
      return;
    }

    const parsedValue = parseFloat(inputValue);

    if (isNaN(parsedValue) || !isFinite(parsedValue)) {
      return; // Invalid number
    }

    // Only update parent when we have a valid complete number
    if (inputModes[index] === "shares") {
      onChange(index, "vc_shares", parsedValue);
      onChange(index, "percentage_ownership", null);
    } else if (inputModes[index] === "percentage") {
      onChange(index, "percentage_ownership", parsedValue);
      onChange(index, "vc_shares", null);
    }
  };

  // Handle blur to finalize the value
  const handleBlur = (index) => {
    const inputValue = rawInputs[index];
    const parsedValue = parseFloat(inputValue);
    
    if (inputValue === "" || inputValue === "." || inputValue === "-" || inputValue === "+") {
      // Clear invalid intermediate states
      onChange(index, "vc_shares", null);
      onChange(index, "percentage_ownership", null);
      const updatedRawInputs = [...rawInputs];
      updatedRawInputs[index] = "";
      setRawInputs(updatedRawInputs);
    } else if (!isNaN(parsedValue) && isFinite(parsedValue)) {
      // Update with the final parsed value and sync the display
      if (inputModes[index] === "shares") {
        onChange(index, "vc_shares", parsedValue);
        onChange(index, "percentage_ownership", null);
      } else if (inputModes[index] === "percentage") {
        onChange(index, "percentage_ownership", parsedValue);
        onChange(index, "vc_shares", null);
      }
      // Update raw input to match the parsed value
      const updatedRawInputs = [...rawInputs];
      updatedRawInputs[index] = String(parsedValue);
      setRawInputs(updatedRawInputs);
    } else {
      // Invalid, clear it
      onChange(index, "vc_shares", null);
      onChange(index, "percentage_ownership", null);
      const updatedRawInputs = [...rawInputs];
      updatedRawInputs[index] = "";
      setRawInputs(updatedRawInputs);
    }
  };

  // Handle the mode toggle (shares or percentage) for each row
  const handleModeChange = (index, mode) => {
    // Reset both fields when the mode changes
    onChange(index, "vc_shares", null);
    onChange(index, "percentage_ownership", null);

    // Update the input mode for the specific row
    const updatedModes = [...inputModes];
    updatedModes[index] = mode; // Update the mode for the specific row
    setInputModes(updatedModes); // Set the updated mode array

    // Set the raw input based on the selected mode and existing prop values
    const updatedRawInputs = [...rawInputs];
    const currentValue = values[index];
    if (mode === "shares" && currentValue.vc_shares != null && currentValue.vc_shares !== undefined) {
      updatedRawInputs[index] = String(currentValue.vc_shares);
    } else if (mode === "percentage" && currentValue.percentage_ownership != null && currentValue.percentage_ownership !== undefined) {
      updatedRawInputs[index] = String(currentValue.percentage_ownership);
    } else {
      updatedRawInputs[index] = "";
    }
    setRawInputs(updatedRawInputs);
  };

  return (
    <tr className="table-row">
      <td className="tooltip-container">
        {label}
        {tooltip && <span className="tooltip-text">{tooltip}</span>}
      </td>
      {values.map((value, index) => (
        <td key={index}>
          <div style={{ display: "flex", alignItems: "center" }}>
            <select
              value={inputModes[index] || ""}
              onChange={(e) => handleModeChange(index, e.target.value)} // Update the mode for the current row
              style={{ marginRight: "8px" }}
              required
            >
              <option value="" disabled>
                Select Mode
              </option>
              <option value="shares">Shares (M)</option>
              <option value="percentage">Ownership (%)</option>
            </select>
            <input
              type="text"
              value={rawInputs[index] || ""}
              onChange={(e) => handleInputChange(index, e.target.value)} // Handle the value change
              onBlur={() => handleBlur(index)} // Finalize value on blur
              required
              disabled={!inputModes[index]} // Disable input until mode is selected
              style={{ width: "100%" }}
            />
          </div>
        </td>
      ))}
    </tr>
  );
};

export default OwnershipTableRow;